import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface ViewerLayoutProps {
    /** Header component */
    header: ReactNode;
    /** Sidebar component */
    sidebar: ReactNode;
    /** Main content area */
    children: ReactNode;
    /** Additional CSS classes for the container */
    className?: string;
}

/**
 * ViewerLayout - Main layout structure for the viewer page.
 *
 * Assembles:
 * - Fixed header at top
 * - Collapsible sidebar on left
 * - Main viewport area taking remaining space
 *
 * Layout structure:
 * ```
 * ┌────────────────────────────────────────────┐
 * │                   Header                   │
 * ├──────────┬─────────────────────────────────┤
 * │          │                                 │
 * │ Sidebar  │         Main Content            │
 * │          │                                 │
 * └──────────┴─────────────────────────────────┘
 * ```
 */
export function ViewerLayout({ header, sidebar, children, className }: ViewerLayoutProps) {
    return (
        <div className={cn('flex h-screen flex-col overflow-hidden bg-background', className)}>
            {/* Fixed header */}
            {header}

            {/* Content area with sidebar and main */}
            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                {sidebar}

                {/* Main viewport area */}
                <main className="flex-1 overflow-hidden bg-muted/10 p-2">{children}</main>
            </div>
        </div>
    );
}
